import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
    Skeleton,
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Alert,
    Backdrop,
    CircularProgress,
    LinearProgress,
    Fade,
    Slide,
    IconButton,
} from '@mui/material';
import {
    Close as CloseIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components for enhanced loading states
const SkeletonCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    overflow: 'hidden',
    '& .MuiSkeleton-root': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const LoadingOverlay = styled(Backdrop)(({ theme }) => ({
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(4px)',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar + 1,
}));

// Toast notification context
const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const toastIdRef = useRef(0);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const addToast = useCallback((message, type = 'info', options = {}) => {
        toastIdRef.current += 1;
        const id = options.id || `toast-${toastIdRef.current}-${Date.now()}`;
        const toast = {
            id,
            message,
            type,
            autoHideDuration: options.autoHideDuration || 6000,
            action: options.action,
            persistent: options.persistent || false,
            ...options,
        };

        setToasts(prev => [...prev, toast]);

        if (!toast.persistent) {
            setTimeout(() => {
                removeToast(id);
            }, toast.autoHideDuration);
        }

        return id;
    }, [removeToast]);

    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    // Convenience methods
    const showSuccess = useCallback((message, options) =>
        addToast(message, 'success', options), [addToast]);

    const showError = useCallback((message, options) =>
        addToast(message, 'error', options), [addToast]);

    const showWarning = useCallback((message, options) =>
        addToast(message, 'warning', options), [addToast]);

    const showInfo = useCallback((message, options) =>
        addToast(message, 'info', options), [addToast]);

    const value = {
        addToast,
        removeToast,
        clearAllToasts,
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
};

// Toast Container Component
const ToastContainer = ({ toasts, onRemove }) => {
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 24,
                right: 24,
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxWidth: 400,
            }}
        >
            {toasts.map((toast, index) => (
                <Slide
                    key={toast.id}
                    direction="left"
                    in={true}
                    timeout={300}
                    style={{ transitionDelay: `${index * 100}ms` }}
                >
                    <Alert
                        severity={toast.type}
                        onClose={() => onRemove(toast.id)}
                        action={
                            toast.action || (
                                <IconButton
                                    size="small"
                                    aria-label="close"
                                    color="inherit"
                                    onClick={() => onRemove(toast.id)}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )
                        }
                        sx={{
                            minWidth: 300,
                            boxShadow: 4,
                            borderRadius: 2,
                            '& .MuiAlert-message': {
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                            },
                        }}
                    >
                        {toast.message}
                    </Alert>
                </Slide>
            ))}
        </Box>
    );
};

// Skeleton Components for different UI elements
export const ProjectCardSkeleton = ({ count = 1 }) => (
    <>
        {Array.from({ length: count }).map((_, index) => (
            <SkeletonCard key={index}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Skeleton variant="text" width="60%" height={32} />
                        <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                    </Box>
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                    <Box sx={{ mt: 2 }}>
                        <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 1 }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                </CardContent>
            </SkeletonCard>
        ))}
    </>
);

export const TimesheetTableSkeleton = ({ rows = 5 }) => (
    <Box>
        {Array.from({ length: rows }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', py: 2, gap: 2 }}>
                <Skeleton variant="text" width="15%" height={24} />
                <Skeleton variant="text" width="25%" height={24} />
                <Skeleton variant="text" width="20%" height={24} />
                <Skeleton variant="text" width="10%" height={24} />
                <Skeleton variant="text" width="15%" height={24} />
                <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
            </Box>
        ))}
    </Box>
);

export const DashboardSkeleton = () => (
    <Box>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width="40%" height={40} />
            <Skeleton variant="text" width="60%" height={24} />
        </Box>

        {/* Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
            {Array.from({ length: 4 }).map((_, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                    <SkeletonCard>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Skeleton variant="circular" width={48} height={48} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="80%" height={24} />
                                    <Skeleton variant="text" width="60%" height={32} />
                                </Box>
                            </Box>
                        </CardContent>
                    </SkeletonCard>
                </Grid>
            ))}
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
                <SkeletonCard>
                    <CardContent>
                        <Skeleton variant="text" width="30%" height={32} />
                        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2, borderRadius: 1 }} />
                    </CardContent>
                </SkeletonCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <SkeletonCard>
                    <CardContent>
                        <Skeleton variant="text" width="40%" height={32} />
                        <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} />
                    </CardContent>
                </SkeletonCard>
            </Grid>
        </Grid>
    </Box>
);

export const NavigationSkeleton = () => (
    <Box sx={{ p: 2 }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width="60%" height={24} />
        </Box>

        {/* Navigation Items */}
        {Array.from({ length: 6 }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="70%" height={20} />
            </Box>
        ))}

        {/* User Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="60%" height={16} />
            </Box>
        </Box>
    </Box>
);

// Loading Overlay Component
export const LoadingOverlayComponent = ({ open, message = 'Loading...' }) => (
    <LoadingOverlay open={open}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <img
                src="/synaptix-pulse-logo.svg"
                alt="synaptix Pulse"
                style={{ width: '60px', height: '60px', marginBottom: '10px' }}
            />
            <CircularProgress size={48} thickness={4} />
            <Typography variant="h6" color="text.primary">
                {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                synaptix Pulse - Professional Services Automation
            </Typography>
        </Box>
    </LoadingOverlay>
);

// Progress Bar Component
export const ProgressBar = ({ progress, show = false, color = 'primary' }) => (
    <Fade in={show}>
        <ProgressContainer>
            <LinearProgress
                variant={progress !== undefined ? 'determinate' : 'indeterminate'}
                value={progress}
                color={color}
                sx={{ height: 3 }}
            />
        </ProgressContainer>
    </Fade>
);

// Error State Components
export const ErrorState = ({
    title = 'Something went wrong',
    message = 'Please try again later',
    action,
    icon: Icon = ErrorIcon,
}) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            textAlign: 'center',
        }}
    >
        <Icon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
            {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
            {message}
        </Typography>
        {action}
    </Box>
);

export const EmptyState = ({
    title = 'No data available',
    message = 'Get started by adding some content',
    action,
    icon: Icon = InfoIcon,
}) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            textAlign: 'center',
        }}
    >
        <Icon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
            {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
            {message}
        </Typography>
        {action}
    </Box>
);

// Loading Button Component
export const LoadingButton = ({
    loading,
    children,
    loadingText = 'Loading...',
    component = 'button',
    ...props
}) => (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Box
            component={component}
            {...props}
            disabled={loading || props.disabled}
            sx={{
                ...props.sx,
                ...(loading && {
                    color: 'transparent',
                }),
            }}
        >
            {loading ? loadingText : children}
        </Box>
        {loading && (
            <CircularProgress
                size={24}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                }}
            />
        )}
    </Box>
);

// Pulse Animation for Loading States
export const PulseLoader = ({ width = '100%', height = 20, count = 3 }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: count }).map((_, index) => (
            <Skeleton
                key={index}
                variant="rectangular"
                width={width}
                height={height}
                animation="pulse"
                sx={{
                    borderRadius: 1,
                    animationDelay: `${index * 0.2}s`,
                }}
            />
        ))}
    </Box>
);

// Shimmer Effect Component
export const ShimmerEffect = ({ children, loading = false }) => {
    if (!loading) return children;

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                    animation: 'shimmer 1.5s infinite',
                },
                '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' },
                },
            }}
        >
            {children}
        </Box>
    );
};

const LoadingSystem = {
    ToastProvider,
    useToast,
    ProjectCardSkeleton,
    TimesheetTableSkeleton,
    DashboardSkeleton,
    NavigationSkeleton,
    LoadingOverlayComponent,
    ProgressBar,
    ErrorState,
    EmptyState,
    LoadingButton,
    PulseLoader,
    ShimmerEffect,
};

export default LoadingSystem; 